import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FilteredStudentsList } from "@/components/FilteredStudentsList";
import { FilterContext } from "@/types/FilterContext";
import { FilterState } from "@/components/FilterSidebar/FilterState";
import { StudentOrdering } from "@/types/StudentOrdering";
import {
  SortDirectionEnum,
  StudentListOrganizationSortableFieldEnum,
} from "@/__generated__/types";
import { FunnelMapKeys, FunnelStateMaps } from "@/util/funnelGroups";
import { Card, CardContent } from "@/components/ui";
import { CircleAlert } from "lucide-react";

interface StudentListProps {
  liveEventId: string;
  selectedBarGroup: FunnelMapKeys<"CURRENT_FUNNEL_STATE_CHART"> | null;
  error?: Error | null;
}

export const StudentList: React.FC<StudentListProps> = ({
  liveEventId,
  selectedBarGroup,
  error = null,
}) => {
  const { t } = useTranslation();

  const filterContext = useMemo(() => {
    const chartFunnelStates = selectedBarGroup
      ? FunnelStateMaps.CURRENT_FUNNEL_STATE_CHART[selectedBarGroup]
      : undefined;

    return new FilterContext("liveEvent", {
      liveEventId,
      liveEventFunnelStates: chartFunnelStates,
    });
  }, [liveEventId, selectedBarGroup]);

  const filterState = useMemo(() => new FilterState({}), []);

  const studentOrdering = useMemo(() => {
    return new StudentOrdering(
      StudentListOrganizationSortableFieldEnum.JoinedAt,
      SortDirectionEnum.Desc,
      "liveEvent",
    );
  }, []);

  if (error) {
    return (
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex h-64 items-center justify-center">
            <div className="flex flex-col items-center space-y-3">
              <CircleAlert className="h-12 w-12 text-muted-foreground" />
              <div className="text-center">
                <div className="mt-1 text-sm text-muted-foreground">
                  {t("eventDetails.results.studentList.errorMessage")}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="mb-6">
      <FilteredStudentsList
        title={t("eventDetails.results.studentList.title")}
        getDescription={(count) => {
          return selectedBarGroup
            ? t("eventDetails.results.studentList.selectedBarDescription", {
                barLabel: t("funnelGroups." + selectedBarGroup),
                totalCount: count,
              })
            : t("eventDetails.results.studentList.description", {
                totalCount: count,
              });
        }}
        filterState={filterState}
        context={filterContext}
        ordering={studentOrdering}
      />
    </div>
  );
};
